import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Pencil, AlertTriangle, CheckCircle2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/lib/auth";
import { Button, Card } from "@/components/ui";
import type { Json } from "@/lib/database.types";
import type { OnboardingContext, OnboardingDraft, StepProps } from "../types";

const STEP_1_REQUIRED: (keyof OnboardingDraft)[] = [
  "first_name", "last_name", "date_of_birth", "mobile", "email",
  "address_line1", "suburb", "state", "postcode",
];
const STEP_2_REQUIRED: (keyof OnboardingDraft)[] = [
  "start_date", "employment_type", "primary_role_name",
];
const STEP_3_REQUIRED: (keyof OnboardingDraft)[] = [
  "next_of_kin_name", "next_of_kin_phone", "next_of_kin_relationship",
];
const STEP_5_REQUIRED_IF_DIRECTOR: (keyof OnboardingDraft)[] = [
  "payroll_group", "bank_bsb", "bank_acct_number",
  "super_fund_name", "super_fund_usi", "super_member_number",
];

const FIELD_LABELS: Partial<Record<keyof OnboardingDraft, string>> = {
  first_name: "First name",
  last_name: "Last name",
  date_of_birth: "Date of birth",
  mobile: "Mobile",
  email: "Email",
  address_line1: "Address line 1",
  suburb: "Suburb",
  state: "State",
  postcode: "Postcode",
  start_date: "Start date",
  employment_type: "Employment type",
  primary_role_name: "Primary role",
  next_of_kin_name: "Next of kin name",
  next_of_kin_phone: "Next of kin phone",
  next_of_kin_relationship: "Next of kin relationship",
  payroll_group: "Payroll group",
  bank_bsb: "BSB",
  bank_acct_number: "Account number",
  super_fund_name: "Super fund name",
  super_fund_usi: "Super USI",
  super_member_number: "Super member number",
};

const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
  full_time: "Full-time",
  part_time: "Part-time",
  casual: "Casual",
  director: "Director",
};

const PAYROLL_GROUP_LABELS: Record<string, string> = {
  director_fortnight: "Director fortnight",
  field_fortnight: "Field staff fortnight",
  casual_weekly: "Casual weekly",
};

// Every key on OnboardingDraft — used to build the merged submit payload in the
// mutation below. The `satisfies Record<keyof OnboardingDraft, 1>` clause makes
// this fail at compile time if a new field is added to OnboardingDraft and not
// mirrored here, or if a key is renamed. See TICKETS.md → T-001.
const DRAFT_KEY_MAP = {
  first_name: 1, middle_name: 1, last_name: 1, preferred_name: 1,
  date_of_birth: 1, mobile: 1, email: 1, home_phone: 1,
  address_line1: 1, address_line2: 1, suburb: 1, state: 1, postcode: 1,
  start_date: 1, employment_type: 1, primary_role_name: 1, subcontractor_company_id: 1,
  next_of_kin_name: 1, next_of_kin_phone: 1, next_of_kin_relationship: 1,
  blood_type: 1, emergency_medical_notes: 1,
  payroll_group: 1, lafha_eligible: 1,
  bank_bsb: 1, bank_acct_number: 1,
  super_fund_name: 1, super_fund_usi: 1, super_member_number: 1,
} as const satisfies Record<keyof OnboardingDraft, 1>;

const ALL_DRAFT_KEYS = Object.keys(DRAFT_KEY_MAP) as (keyof OnboardingDraft)[];

interface Validation {
  valid: boolean;
  missingByStep: Record<number, (keyof OnboardingDraft)[]>;
}

function isFilled(v: unknown): boolean {
  if (typeof v === "string") return v.trim().length > 0;
  return v !== undefined && v !== null;
}

function validateDraft(
  draft: OnboardingDraft,
  emp: OnboardingContext["employee"],
  isDirector: boolean,
): Validation {
  const missingByStep: Record<number, (keyof OnboardingDraft)[]> = {};
  // Cast once so the filter body stays readable. keyof OnboardingDraft is a
  // subset of keyof EmployeeRow for every required field, so the widened read
  // is safe in practice.
  const empRecord = emp as Record<string, unknown> | null;
  const check = (step: number, fields: (keyof OnboardingDraft)[]) => {
    const missing = fields.filter(f => !isFilled(draft[f] ?? empRecord?.[f]));
    if (missing.length) missingByStep[step] = missing;
  };
  check(1, STEP_1_REQUIRED);
  check(2, STEP_2_REQUIRED);
  check(3, STEP_3_REQUIRED);
  if (isDirector) check(5, STEP_5_REQUIRED_IF_DIRECTOR);
  return { valid: Object.keys(missingByStep).length === 0, missingByStep };
}

export default function Step6Review({
  context, draft, setCurrentStep, clearDraft,
}: StepProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validation = validateDraft(draft, context.employee, context.is_director);
  const targetAuthUserId = context.user?.auth_user_id ?? null;

  // Same queryKey as Step 2 — shared cache, deduped by React Query.
  const { data: roles } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("roles")
        .select("role_name, display_name")
        .eq("is_employee_role", true)
        .order("sort_order");
      if (error) throw error;
      return data ?? [];
    },
  });

  const roleDisplay = (roleName: string | null | undefined): string => {
    if (!roleName) return "—";
    return roles?.find(r => r.role_name === roleName)?.display_name ?? roleName;
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!targetAuthUserId) {
        throw new Error("Missing user context — cannot submit. Refresh and try again.");
      }
      // T-001: merge draft with existing employee row. Fields that were prefilled
      // but never retyped only live on `context.employee`, not on `draft` — the
      // wizard only writes keys the user touches. Without this merge, the RPC's
      // direct-assignment UPDATE for required columns evaluates to null and hits
      // the NOT NULL constraint. Mirrors the fallback chain used by validateDraft()
      // and the v() display helper — three-way consistency.
      const empRecord = context.employee as Record<string, unknown> | null;
      const merged: Record<string, unknown> = {};
      for (const k of ALL_DRAFT_KEYS) {
        const val = draft[k] ?? empRecord?.[k];
        if (val !== undefined && val !== null) merged[k] = val;
      }
      const { data, error } = await supabase.schema("app").rpc("complete_onboarding", {
        p_target_user_id: targetAuthUserId,
        p_payload: merged as unknown as Json,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["onboarding_context"] });
      // Self-onboarding: patch the auth store so OnboardingGate lets us through to "/"
      // without requiring a full page reload. Also stamp employee_id in case the RPC
      // just created it on the INSERT path. On-behalf onboarding doesn't touch the
      // acting director's auth store — they're already onboarded.
      if (context.is_self) {
        const store = useAuthStore.getState();
        if (store.user) {
          const newEmployeeId = data as string | null;
          store.setUser({
            ...store.user,
            onboarding_completed_at: new Date().toISOString(),
            employee_id: newEmployeeId ?? store.user.employee_id,
          });
        }
      }
      // Delay so the success screen (below) is actually visible. Clear the draft
      // just before navigating so Step 6 stays mounted for the full window.
      setTimeout(() => {
        clearDraft?.();
        navigate("/");
      }, 1500);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error
        ? err.message
        : (err as { message?: string })?.message ?? "Submit failed — please try again.";
      setSubmitError(msg);
    },
  });

  const jumpToStep = (n: number) => () => setCurrentStep?.(n);

  const onSubmit = () => {
    setSubmitError(null);
    mutation.mutate();
  };

  if (mutation.isSuccess) {
    return (
      <div className="py-8 text-center space-y-3">
        <CheckCircle2 size={48} className="mx-auto text-ok" />
        <h2 className="text-xl font-semibold">You're all set</h2>
        <p className="text-text-muted text-sm">
          {context.is_self ? "Welcome to Coretel Ops." : "Onboarding complete."}
        </p>
      </div>
    );
  }

  // --- Display helpers: pull from draft with employee-row fallback ---------
  const emp = context.employee;
  const v = <K extends keyof OnboardingDraft>(key: K): string => {
    const d = draft[key];
    if (typeof d === "string" && d.length > 0) return d;
    if (emp) {
      const e = (emp as Record<string, unknown>)[key];
      if (typeof e === "string" && e.length > 0) return e;
    }
    return "";
  };

  const nameParts = [v("first_name"), v("middle_name"), v("last_name")]
    .filter(Boolean).join(" ");
  const preferred = v("preferred_name");
  const displayName = preferred ? `${nameParts} (${preferred})` : nameParts;

  const cityLine = [v("suburb"), v("state"), v("postcode")].filter(Boolean).join(" ");
  const addressLine = [v("address_line1"), v("address_line2"), cityLine]
    .filter(Boolean).join(", ");

  const lafhaEligible = draft.lafha_eligible ?? emp?.lafha_eligible ?? false;

  return (
    <div className="space-y-4">
      <p className="text-text-muted text-sm">
        Review your details before submitting. Click Edit to jump back to any section.
      </p>

      {!validation.valid && (
        <Card className="border-warn/40">
          <div className="p-4">
            <div className="flex items-center gap-2 text-warn mb-2">
              <AlertTriangle size={14} />
              <span className="text-sm font-medium">Missing required details</span>
            </div>
            <ul className="text-sm text-text-muted space-y-1">
              {Object.entries(validation.missingByStep).map(([step, fields]) => (
                <li key={step}>
                  <button
                    onClick={jumpToStep(Number(step))}
                    className="text-accent hover:underline"
                  >
                    Step {step}
                  </button>
                  : {fields.map(f => FIELD_LABELS[f] ?? f).join(", ")}
                </li>
              ))}
            </ul>
          </div>
        </Card>
      )}

      <ReviewCard title="Personal details" onEdit={jumpToStep(1)}>
        <LabelValue label="Name" value={displayName} />
        <LabelValue label="Date of birth" value={v("date_of_birth")} />
        <LabelValue label="Mobile" value={v("mobile")} />
        <LabelValue label="Email" value={v("email")} />
        {v("home_phone") && <LabelValue label="Home phone" value={v("home_phone")} />}
        <LabelValue label="Address" value={addressLine} />
      </ReviewCard>

      <ReviewCard title="Employment" onEdit={jumpToStep(2)}>
        <LabelValue label="Start date" value={v("start_date")} />
        <LabelValue
          label="Employment type"
          value={EMPLOYMENT_TYPE_LABELS[v("employment_type")] ?? v("employment_type")}
        />
        <LabelValue label="Primary role" value={roleDisplay(v("primary_role_name"))} />
      </ReviewCard>

      <ReviewCard title="Next of kin" onEdit={jumpToStep(3)}>
        <LabelValue label="Name" value={v("next_of_kin_name")} />
        <LabelValue label="Phone" value={v("next_of_kin_phone")} />
        <LabelValue label="Relationship" value={v("next_of_kin_relationship")} />
      </ReviewCard>

      <ReviewCard title="Emergency medical" onEdit={jumpToStep(4)}>
        <LabelValue label="Blood type" value={v("blood_type") || "Not provided"} />
        <LabelValue label="Notes" value={v("emergency_medical_notes") || "None"} />
      </ReviewCard>

      {context.is_director && (
        <ReviewCard title="Payroll" onEdit={jumpToStep(5)}>
          <LabelValue
            label="Payroll group"
            value={PAYROLL_GROUP_LABELS[v("payroll_group")] ?? v("payroll_group")}
          />
          <LabelValue label="LAFHA eligible" value={lafhaEligible ? "Yes" : "No"} />
          <LabelValue label="BSB" value={v("bank_bsb")} />
          <LabelValue label="Account number" value={v("bank_acct_number")} />
          <LabelValue label="Super fund" value={v("super_fund_name")} />
          <LabelValue label="Super USI" value={v("super_fund_usi")} />
          <LabelValue label="Member number" value={v("super_member_number")} />
        </ReviewCard>
      )}

      {submitError && <div className="text-danger text-sm">{submitError}</div>}

      <Button
        variant="primary"
        size="lg"
        disabled={!validation.valid || mutation.isPending}
        onClick={onSubmit}
      >
        {mutation.isPending ? "Submitting…" : "Submit"}
      </Button>
    </div>
  );
}

// ----------------------------------------------------------------------------

interface ReviewCardProps {
  title: string;
  onEdit: () => void;
  children: ReactNode;
}

function ReviewCard({ title, onEdit, children }: ReviewCardProps) {
  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[11px] font-semibold text-text-muted uppercase tracking-wider2">
            {title}
          </h3>
          <Button variant="ghost" size="sm" icon={Pencil} onClick={onEdit}>
            Edit
          </Button>
        </div>
        <div className="divide-y divide-line">{children}</div>
      </div>
    </Card>
  );
}

function LabelValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-1 text-sm">
      <span className="text-text-muted">{label}</span>
      <span className={value ? "" : "text-text-dim"}>{value || "—"}</span>
    </div>
  );
}
